import { Event } from '@/types/residents/event';
import { eventStrings } from '@/constants/residents/strings';

// ============================================================================
// DATE & TIME FORMATTING
// ============================================================================

/**
 * Format date for mobile display (e.g., "Aug 23, 2023")
 */
export const formatEventDateShort = (dateString?: string): string => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format date for full display (e.g., "Wednesday, August 23, 2023")
 */
export const formatEventDateFull = (dateString?: string): string => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format time range for display (e.g., "1:00 pm - 3:00 pm")
 */
export const formatEventTimeRange = (startTime?: string, endTime?: string): string => {
  if (!startTime) return 'TBD';
  
  const start = new Date(startTime);
  const startStr = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).toLowerCase();
  
  if (!endTime) return startStr;
  
  const end = new Date(endTime);
  const endStr = end.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).toLowerCase();
  
  return `${startStr} - ${endStr}`;
};

/**
 * Format date and time combined (e.g., "Wednesday, August 23, 2023 at 1:00 PM")
 */
export const formatEventDateTime = (dateString?: string): string => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// ============================================================================
// EVENT BADGE/LABEL
// ============================================================================

export type EventBadgeStyle = 'attending' | 'gradient' | 'neutral' | 'full';

export interface EventBadgeInfo {
  text: string;
  style: EventBadgeStyle;
  showCheckmark?: boolean;
}

/**
 * Get the badge/label info for an event based on RSVP status
 * Returns null if no badge should be shown (e.g., past events)
 */
export const getEventBadgeInfo = (event: Event, isPast?: boolean): EventBadgeInfo | null => {
  if (isPast) return null;
  
  // If user is attending, show green "Attending" badge
  if (event.rsvp) {
    return { 
      text: 'Attending', 
      style: 'attending',
      showCheckmark: true,
    };
  }
  
  const total = event.rsvpLimit ?? 0;
  const taken = event.nRsvps ?? 0;
  const remaining = total - taken;
  const isInfinite = total === 0;
  
  // No limit - show neutral RSVP badge
  if (isInfinite) {
    return { text: 'RSVP', style: 'neutral' };
  }
  
  // Has spots available - show gradient "X spots left!" badge
  if (remaining > 0) {
    const spotsText = remaining === 1 
      ? eventStrings.subtitle.spotsLeftSingular 
      : eventStrings.subtitle.spotsLeft(remaining);
    return { text: `${spotsText}!`, style: 'gradient' };
  }
  
  // Full - show "Full" badge
  return { text: 'Full', style: 'full' };
};

// ============================================================================
// CAPACITY & RSVP
// ============================================================================

/**
 * Get capacity text (e.g., "25/50" or "25 / No Limit")
 * Returns null if no limit is set
 */
export const getEventCapacityText = (event: Event): string | null => {
  const total = event.rsvpLimit ?? 0;
  const taken = event.nRsvps ?? 0;
  
  if (total <= 0) return null;
  return `${taken}/${total}`;
};

/**
 * Get full capacity display text (e.g., "25 / 50" or "25 / No Limit")
 * Always returns a string
 */
export const getEventCapacityDisplay = (event: Event): string => {
  const total = event.rsvpLimit ?? 0;
  const taken = event.nRsvps ?? 0;
  const isInfinite = total === 0;
  
  return `${taken} / ${isInfinite ? 'No Limit' : total}`;
};

/**
 * Get remaining spots count
 */
export const getEventRemainingSpots = (event: Event): number => {
  const total = event.rsvpLimit ?? 0;
  const taken = event.nRsvps ?? 0;
  return Math.max(0, total - taken);
};

/**
 * Check if event is full
 */
export const isEventFull = (event: Event): boolean => {
  const total = event.rsvpLimit ?? 0;
  if (total === 0) return false; // No limit means never full
  return getEventRemainingSpots(event) <= 0;
};

// ============================================================================
// CONTENT EXTRACTION
// ============================================================================

/**
 * Get event description from either description or responseDescription field
 */
export const getEventDescription = (event: Event): string => {
  return event.description || event.responseDescription || '';
};

/**
 * Get first photo URL from event
 */
export const getEventImageUrl = (event: Event): string | undefined => {
  return event.photos && event.photos.length > 0 
    ? event.photos[0]?.photoUrl 
    : undefined;
};

// ============================================================================
// EVENT DETAILS FOR SHARING
// ============================================================================

/**
 * Format event details for sharing (e.g., via Share API)
 */
export const formatEventDetailsForSharing = (event: Event): string => {
  const name = event.name || 'Event';
  const date = event.startTime ? formatEventDateFull(event.startTime) : 'TBD';
  const time = event.startTime
    ? new Date(event.startTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';
  const location = event.location || '';
  const description = getEventDescription(event);
  
  let details = `📅 ${name}\n\n📆 ${date}`;
  if (time) details += ` at ${time}`;
  if (location) details += `\n📍 ${location}`;
  if (description) details += `\n\n${description}`;
  
  return details;
};

// ============================================================================
// EVENT STATUS CHECKS
// ============================================================================

/**
 * Check if event is in the past
 */
export const isEventPast = (event: Event): boolean => {
  if (!event.startTime) return false;
  return new Date(event.startTime) < new Date();
};

/**
 * Check if event is upcoming
 */
export const isEventUpcoming = (event: Event): boolean => {
  if (!event.startTime) return true; // Events without dates shown as upcoming
  return new Date(event.startTime) >= new Date();
};
